/**
 * Button State Configuration based on request status and permission flags
 * Implements Confluence business logic for enabled/disabled button states
 * Compliant with Spec: Branch #1 (Normal), Branch #2 (On-Hold), Branch #3 (Override)
 */

export interface ButtonStateConfig {
  approvedDisabled: boolean
  deniedDisabled: boolean
  holdDisabled: boolean
  returnedDisabled: boolean
  partiallyApprovedDisabled: boolean
  partiallyApprovedChecked?: boolean
  returnedChecked?: boolean
  approvedChecked?: boolean
  deniedChecked?: boolean
  holdChecked?: boolean
  hideHoldOptions?: boolean
  hideDenialReasons?: boolean
  showDenialReasons?: boolean
  hideAprvrDefaultSelErrMsg?: boolean
  handlePartialApprovedOption?: boolean
  handleFullApprovedOption?: boolean
  enableOverrideSubmitButton?: boolean
  showOverrideForm?: boolean
  hidePartialApprovalOption?: boolean
}

export interface ButtonStateOptions {
  status: string
  isPartialApprovalAllowed: boolean
  isFullApprovalAllowed: boolean
  isHoldAllowed: boolean
  isOverrideAllowed: boolean
  isOnHold?: boolean
  allDaysOverlap?: boolean
  isRetractionFlow?: boolean
  isPartialRetractionFlow?: boolean
  isPastRequest?: boolean
}

// ============ HELPER FUNCTIONS FOR STATUS CHECKS ============

/**
 * Check if status is an override/retraction denied state (Branch #3)
 */
const isRetractionDeniedStatus = (statusUpper: string): boolean =>
  [
    'RTDENIED',
    'PRTDENIED',
    'RTDENIED_OVERRIDDEN',
    'OVERRIDDEN_RTDENIED',
    'TORPAPPROVEDRETRACTIONDENIED',
    'PAPPROVED_RTDENIED',
  ].includes(statusUpper)

/**
 * Check if status is an on-hold variant (Branch #2)
 */
const isOnHoldStatus = (statusUpper: string): boolean =>
  [
    'SUBMITTED_ONHOLD',
    'RESUBMITTED_ONHOLD',
    'RTSUBMITTED_ONHOLD',
    'PRTSUBMITTED_ONHOLD',
  ].includes(statusUpper)

const getRetractionDeniedOverrideConfig = (): ButtonStateConfig => ({
  approvedDisabled: false,
  approvedChecked: true,

  deniedDisabled: true,
  holdDisabled: true,
  returnedDisabled: true,

  partiallyApprovedDisabled: true,
  hidePartialApprovalOption: true,

  showOverrideForm: true,
  enableOverrideSubmitButton: true,

  hideAprvrDefaultSelErrMsg: true,
})

/**
 * Check if status is a denied variant
 */
const isDeniedStatus = (statusUpper: string): boolean =>
  ['DENIED', 'PDENIED',].includes(statusUpper)

/**
 * Check if status allows return action
 */

  const isReturnedAllowedStatus = (statusUpper: string): boolean =>
  [
    'SUBMITTED',
    'SUBMITTED_RELEASED',
    'RESUBMITTED',
    'RESUBMITTED_RELEASED',
    'SUBMITTED_ONHOLD',
    'RESUBMITTED_ONHOLD',
  ].includes(statusUpper)
/**
 * Check if status is a normal submitted request (non-hold)
 */


// ============ BRANCH #3: OVERRIDE/RETRACTION DENIED FORM ============

/**
 * Branch #3: Override/Retraction Denied States (RTDENIED / PRTDENIED)
 * Approved: ✅ pre-checked | Denied: 🔒 | Hold: 🔒 | Returned: 🔒 | Partial: —
 */

// ============ BRANCH #1 & #2: NO PERMISSIONS CASE ============

/**
 * No Permissions: Only Returned works
 * Approved: 🔒 | Denied: 🔒 | Hold: 🔒 | Returned: ✅🔘 | Partial: 🔒
 */
const getNoPermissionsConfig = (): ButtonStateConfig => ({
  approvedDisabled: true,
  deniedDisabled: true,
  holdDisabled: true,

  returnedDisabled: false,
  returnedChecked: true,

  partiallyApprovedDisabled: true,

  hidePartialApprovalOption: true,
  hideDenialReasons: true,

  hideAprvrDefaultSelErrMsg: true,
})


  const getDeniedOverrideConfig = (): ButtonStateConfig => ({
  approvedDisabled: false,
  approvedChecked: true,

  deniedDisabled: true,
  deniedChecked: false,

  holdDisabled: true,
  returnedDisabled: true,

  partiallyApprovedDisabled: true,
  hidePartialApprovalOption: true,

  showOverrideForm: false,
  enableOverrideSubmitButton: true,

  hideAprvrDefaultSelErrMsg: true,
})
// ============ BRANCH #1: NORMAL FLOW ============

/**
 * Normal Flow - No Overlap: All options enabled per permission flags
 * Approved: ✅ | Denied: ✅ | Hold: ✅ | Returned: ✅🔘 | Partial: ✅
 */
const getNormalNoOverlapConfig = (
  statusUpper: string,
  isFullApprovalAllowed: boolean,
  isPartialApprovalAllowed: boolean,
  isHoldAllowed: boolean
): ButtonStateConfig => ({
  approvedDisabled: !isFullApprovalAllowed,
  deniedDisabled: false,
  holdDisabled: !isHoldAllowed,
  returnedDisabled: !isReturnedAllowedStatus(statusUpper),
  partiallyApprovedDisabled: !isPartialApprovalAllowed,
  partiallyApprovedChecked: isPartialApprovalAllowed && !isFullApprovalAllowed,
  hideAprvrDefaultSelErrMsg: false,
  handleFullApprovedOption: isFullApprovalAllowed,
  handlePartialApprovedOption: isPartialApprovalAllowed,
})

/**
 * Normal Flow - All Days Overlap: Limited options per spec
 * Approved: ⬜ | Denied: ✅🔘 | Hold: ⬜ | Returned: ✅ | Partial: ⬜
 */
const getNormalAllOverlapConfig = (): ButtonStateConfig => ({
  approvedDisabled: true,
  deniedDisabled: false,
  deniedChecked: true,
  holdDisabled: true,
  returnedDisabled: false,
  partiallyApprovedDisabled: true,
  showDenialReasons: true,
  hideAprvrDefaultSelErrMsg: true,
})

/**
 * DENIED Status - Case 1: isOverrideAllowed = true
 * Approved: ✅ | Denied: 🔒 | Hold: 🔒 | Returned: 🔒 | Partial: —
 */

/**
 * DENIED Status - Case 2: isOverrideAllowed = false AND isFullApprovalAllowed = true
 * Approved: ✅ | Denied: 🔒 | Hold: 🔒 | Returned: ✅ | Partial: ✅
 */
const getDeniedFullApprovalConfig = (
  statusUpper: string,
  isPartialApprovalAllowed: boolean
): ButtonStateConfig => ({
  approvedDisabled: true,
  deniedDisabled: true,
  holdDisabled: true,
  returnedDisabled: !isReturnedAllowedStatus(statusUpper),
  partiallyApprovedDisabled: !isPartialApprovalAllowed,
  hideDenialReasons: true,
  handleFullApprovedOption: true,
  hideAprvrDefaultSelErrMsg: true,
})

/**
 * DENIED Status - Case 3: isOverrideAllowed = false AND isFullApprovalAllowed = false AND isPartialApprovalAllowed = true AND isHoldAllowed = true
 * Approved: 🔒 | Denied: ✅ | Hold: ✅ | Returned: ✅ | Partial: ✅
 */
const getDeniedPartialOnlyConfig = (isHoldAllowed: boolean): ButtonStateConfig => ({
  approvedDisabled: true,
  deniedDisabled: false,
  deniedChecked: true,
  holdDisabled: !isHoldAllowed,
  returnedDisabled: false,
  partiallyApprovedDisabled: false,
  partiallyApprovedChecked: true,
  showDenialReasons: true,
  handlePartialApprovedOption: true,
  handleFullApprovedOption: false,
  hideAprvrDefaultSelErrMsg: true,
})

// ============ BRANCH #2: ON-HOLD FLOW ============

/**
 * On-Hold - Default: All options enabled, Hold pre-checked
 * Approved: ✅ | Denied: ✅ | Hold: ✅🔘 | Returned: ✅ | Partial: ✅
 */
const getOnHoldDefaultConfig = (
  statusUpper: string,
  isFullApprovalAllowed: boolean,
  isPartialApprovalAllowed: boolean,
  isHoldAllowed: boolean
): ButtonStateConfig => ({
  approvedDisabled: !isFullApprovalAllowed,
  deniedDisabled: false,
  holdDisabled: !isHoldAllowed,
  holdChecked: true,
  returnedDisabled: !isReturnedAllowedStatus(statusUpper),
  partiallyApprovedDisabled: !isPartialApprovalAllowed,
  hideAprvrDefaultSelErrMsg: true,
  handleFullApprovedOption: isFullApprovalAllowed,
})

/**
 * On-Hold + DENIED - Case 1: isOverrideAllowed = true
 * Approved: ✅ | Denied: ✅ | Hold: ✅ | Returned: ✅ | Partial: ✅
 */
const getOnHoldDeniedOverrideConfig = (
  isPartialApprovalAllowed: boolean,
  isHoldAllowed: boolean
): ButtonStateConfig => ({
  approvedDisabled: false,
  deniedDisabled: false,
  holdDisabled: !isHoldAllowed,
  returnedDisabled: false,
  partiallyApprovedDisabled: !isPartialApprovalAllowed,
  enableOverrideSubmitButton: true,
  showDenialReasons: true,
  hideAprvrDefaultSelErrMsg: true,
})
const getPastDeniedConfig = (): ButtonStateConfig => ({
 approvedDisabled:true,
 deniedDisabled:true,
 holdDisabled:true,
 returnedDisabled:true,
 partiallyApprovedDisabled:true,
 hideDenialReasons:true,
 hideAprvrDefaultSelErrMsg:true,
})

/**
 * On-Hold + DENIED - Case 2: isOverrideAllowed = false
 * Approved: 🔒 | Denied: ✅ | Hold: ✅ | Returned: ✅ | Partial: ✅
 */
const getOnHoldDeniedNormalConfig = (
  isPartialApprovalAllowed: boolean,
  isFullApprovalAllowed: boolean,
  isHoldAllowed: boolean
): ButtonStateConfig => ({
  approvedDisabled: !isFullApprovalAllowed,
  deniedDisabled: false,
  holdDisabled: !isHoldAllowed,
  returnedDisabled: false,
  partiallyApprovedDisabled: !isPartialApprovalAllowed,
  showDenialReasons: true,
  hideAprvrDefaultSelErrMsg: true,
})
// ============ RETRACTION FLOW HELPERS ============

/**
 * Retraction flows: Returned always disabled, Partial never shown
 * Applied to RTSUBMITTED/RTSUBMITTED_RELEASED/RTSUBMITTED_ONHOLD/PRTSUBMITTED variants
 */
const applyRetractionRestrictions = (
  config: ButtonStateConfig
): ButtonStateConfig => ({
  ...config,
  returnedDisabled: true,
  partiallyApprovedDisabled: true,
  hidePartialApprovalOption: true,
})
/**
 * Compute button state configuration based on request status and permissions
 * Implements all Confluence branches: #1 (Normal), #2 (On-Hold), #3 (Override)
 *
 * @param status - Current request status
 * @param isCurrentRequest - TRUE if request dates are in future, FALSE if past
 * @param isPartialApprovalAllowed - Permission flag
 * @param isFullApprovalAllowed - Permission flag
 * @param isHoldAllowed - Permission flag
 * @param isOverrideAllowed - Permission flag
 * @param isOnHold - Whether the request is currently on hold
 * @param allDaysOverlap - Whether all requested dates overlap with holidays
 * @param isRetractionFlow - Whether this is a retraction (RTSUBMITTED*)
 * @param isPartialRetractionFlow - Whether this is a partial retraction (PRTSUBMITTED*)
 * @returns Button state configuration per Confluence spec
 */
export const getButtonStateConfig = (
  options: ButtonStateOptions
): ButtonStateConfig => {
  const {
    status,
    isPartialApprovalAllowed,
    isFullApprovalAllowed,
    isHoldAllowed,
    isOverrideAllowed,
    isOnHold = false,
    allDaysOverlap = false,
    isRetractionFlow = false,
    isPartialRetractionFlow = false,
    isPastRequest = false,
  } = options

  const statusUpper = status?.toUpperCase() || ''
if (isRetractionDeniedStatus(statusUpper) &&
  isOverrideAllowed) {
  return getRetractionDeniedOverrideConfig()
}


if (
  isPastRequest &&
  isDeniedStatus(statusUpper)
) {
  return getPastDeniedConfig()
}

  if (isOnHold && isOnHoldStatus(statusUpper)) {
    return getOnHoldConfig({
      statusUpper,
      isPartialApprovalAllowed,
      isFullApprovalAllowed,
      isHoldAllowed,
      isOverrideAllowed,
      isRetractionFlow,
      isPartialRetractionFlow,
    })
  }

  return getNormalFlowConfig({
    statusUpper,
    isPartialApprovalAllowed,
    isFullApprovalAllowed,
    isHoldAllowed,
    isOverrideAllowed,
    allDaysOverlap,
    isRetractionFlow,
    isPartialRetractionFlow,
  })
}
interface OnHoldConfigParams {
  statusUpper: string
  isPartialApprovalAllowed: boolean
  isFullApprovalAllowed: boolean
  isHoldAllowed: boolean
  isOverrideAllowed: boolean
  isRetractionFlow: boolean
  isPartialRetractionFlow: boolean
}

const getOnHoldConfig = ({
  statusUpper,
  isPartialApprovalAllowed,
  isFullApprovalAllowed,
  isOverrideAllowed,
  isRetractionFlow,
  isPartialRetractionFlow,
  isHoldAllowed,
}: OnHoldConfigParams): ButtonStateConfig => {
  if (!isPartialApprovalAllowed && !isFullApprovalAllowed) {
    return getNoPermissionsConfig()
  }

  if (isDeniedStatus(statusUpper)) {
    return isOverrideAllowed
      ? getOnHoldDeniedOverrideConfig(isPartialApprovalAllowed, isHoldAllowed)
      : getOnHoldDeniedNormalConfig(
          isPartialApprovalAllowed,
          isFullApprovalAllowed,
          isHoldAllowed
        )
  }

  let config = getOnHoldDefaultConfig(
    statusUpper,
    isFullApprovalAllowed,
    isPartialApprovalAllowed,
    isHoldAllowed
  )

  if (isRetractionFlow || isPartialRetractionFlow) {
    config = applyRetractionRestrictions(config)
  }

  return config
}
interface NormalFlowConfigParams {
  statusUpper: string
  isPartialApprovalAllowed: boolean
  isFullApprovalAllowed: boolean
  isHoldAllowed: boolean
  isOverrideAllowed: boolean
  allDaysOverlap: boolean
  isRetractionFlow: boolean
  isPartialRetractionFlow: boolean
}

const getNormalFlowConfig = ({
  statusUpper,
  isPartialApprovalAllowed,
  isFullApprovalAllowed,
  isHoldAllowed,
  isOverrideAllowed,
  allDaysOverlap,
  isRetractionFlow,
  isPartialRetractionFlow,
}: NormalFlowConfigParams): ButtonStateConfig => {
  if (!isPartialApprovalAllowed && !isFullApprovalAllowed) {
    return getNoPermissionsConfig()
  }

  if (isDeniedStatus(statusUpper)) {
    return getDeniedStatusConfig(
      statusUpper,
      isOverrideAllowed,
      isFullApprovalAllowed,
      isPartialApprovalAllowed,
      isHoldAllowed
    )
  }

  let config: ButtonStateConfig

  if (allDaysOverlap) {
    config = getNormalAllOverlapConfig()
  } else {
    config = getNormalNoOverlapConfig(
      statusUpper,
      isFullApprovalAllowed,
      isPartialApprovalAllowed,
      isHoldAllowed
    )
  }

  if (isRetractionFlow || isPartialRetractionFlow) {
    config = applyRetractionRestrictions(config)
  }

  return config
}

const getDeniedStatusConfig = (
  statusUpper: string,
  isOverrideAllowed: boolean,
  isFullApprovalAllowed: boolean,
  isPartialApprovalAllowed: boolean,
  isHoldAllowed: boolean
): ButtonStateConfig => {
  // Case 1:
  // DENIED/PDENIED/PRTDENIED + Override allowed
  // Approved enabled

if (isOverrideAllowed) {
    return getDeniedOverrideConfig()
}

  // Case 2:
  // DENIED/PDENIED/PRTDENIED + Override not allowed
  // If either approval permission exists:
  // Approved MUST be disabled
  if (isPartialApprovalAllowed && !isFullApprovalAllowed && isHoldAllowed) {
    return getDeniedPartialOnlyConfig(isHoldAllowed )
  }

  if (isFullApprovalAllowed || isPartialApprovalAllowed) {
    return getDeniedFullApprovalConfig(
    statusUpper,
    isPartialApprovalAllowed
  )
  }

  // Case 3:
  // No full approval, partial approval only, hold allowed

  // Case 4:
  // No approval permissions
  return getNoPermissionsConfig()
}
